import { apiFetch, isApiMode } from './http';

// Client for the permission matrix (/platform/roles, /platform/capabilities).
//
// Everything here is a VIEW of what the server decided. The page mirrors the
// server's read/write split so it does not offer a control the API will
// refuse — but the refusal is the API's, always.

export interface CapabilityInfo {
  key: string;
  /**
   * THE SERVER'S WORD, NOT A CLIENT INVENTION.
   *
   * `/platform/capabilities` returns `CAPABILITY_DERIVATION[key]` verbatim,
   * and that type is `'platform' | 'organization' | 'identity'`. This
   * interface used to say `'role' | 'identity'` and the matrix filtered on
   * `=== 'role'` — a value the server has never sent. The filter matched
   * nothing, the grid rendered ZERO capability columns, and the page looked
   * fine: roles listed, nothing to tick.
   *
   * The test fake supplied `'role'` too, so the fake agreed with the client
   * while both disagreed with the server. That is the shape CLAUDE.md names
   * from the `/pad/state` episode — empty results while every screen still
   * renders — and it is why `derivation` is spelled from the server's own
   * union here rather than from what the page wishes it were.
   *
   * `identity` capabilities are never offered as columns: the server refuses
   * to put one in a role, because participation is not something an
   * administrator hands out.
   */
  derivation: 'platform' | 'organization' | 'identity' | 'link';
  /**
   * What a holder can DO, in the product's own words — the server's
   * `CAPABILITY_DESCRIPTION`. Optional because an older API will not send it;
   * the grid falls back to the identifier rather than rendering nothing.
   */
  description?: string;
  /**
   * Does any route ask for this capability today? The catalog runs ahead of
   * the product on purpose, so a tick is not always a change in what somebody
   * can do; the grid labels the dormant ones. Optional: an older API will not
   * send it, and absent means «unknown», which is drawn as nothing.
   */
  inForce?: boolean;
}

/** Capabilities a ROLE may hold — everything the server will not refuse. */
export const isRoleAssignable = (c: CapabilityInfo): boolean => c.derivation !== 'identity';

export interface RoleView {
  id: string;
  key: string;
  name: string;
  description: string | null;
  scope: 'PLATFORM' | 'ORGANIZATION' | 'LINK' | string;
  appliesTo: 'CENTRE' | 'CLUB' | null | string;
  ownerTenantId: string | null;
  ownerName: string | null;
  isSystem: boolean;
  /**
   * `PLATFORM` — offered to every organization. `SELECTED` — only to
   * `organizations`. A SEPARATE AXIS from `scope`: that is the authority class,
   * this is reach.
   */
  availability: 'PLATFORM' | 'SELECTED' | string;
  /**
   * The organizations this role is offered to. Empty under `SELECTED` means
   * offered NOWHERE — a real state, not a synonym for «everywhere».
   */
  organizations: { id: string; name: string }[];
  capabilities: string[];
  holders: number;
}

export type MatrixResult =
  | { state: 'no-api' }
  | { state: 'ok'; roles: RoleView[]; capabilities: CapabilityInfo[] }
  | { state: 'forbidden' }
  | { state: 'error'; status: number | null };

export async function fetchMatrix(): Promise<MatrixResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const [rolesRes, capsRes] = await Promise.all([
      apiFetch('/platform/roles'),
      apiFetch('/platform/capabilities'),
    ]);
    if (rolesRes.status === 401 || rolesRes.status === 403) return { state: 'forbidden' };
    if (!rolesRes.ok) return { state: 'error', status: rolesRes.status };
    if (!capsRes.ok) return { state: 'error', status: capsRes.status };
    const roles = ((await rolesRes.json()) as { roles: RoleView[] }).roles;
    const capabilities = ((await capsRes.json()) as { capabilities: CapabilityInfo[] })
      .capabilities;
    return { state: 'ok', roles, capabilities };
  } catch {
    return { state: 'error', status: null };
  }
}

export type RoleChangeResult =
  | { state: 'ok'; role: RoleView }
  | { state: 'forbidden' }
  /** The server refused with a named reason (e.g. `identity_capability`, `system_role`). */
  | { state: 'refused'; reason: string }
  | { state: 'error'; status: number | null };

async function roleRequest(path: string, init: RequestInit): Promise<RoleChangeResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(path, init);
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 400 || res.status === 409) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      return { state: 'refused', reason: body?.error ?? 'refused' };
    }
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { role: RoleView };
    return { state: 'ok', role: body.role };
  } catch {
    return { state: 'error', status: null };
  }
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/** Replace a role's whole capability set — one matrix row. */
export function updateRoleCapabilities(
  roleId: string,
  capabilities: string[],
): Promise<RoleChangeResult> {
  return roleRequest(`/platform/roles/${encodeURIComponent(roleId)}/capabilities`, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ capabilities }),
  });
}

/**
 * Rename a role, or change what it is for.
 *
 * NOT THE KEY, and not the capabilities. The key is an identifier the
 * authorization service names (`PLATFORM_SUPERADMIN` by key for its bypass)
 * and deployments pin by id — the server refuses to change it, and this does
 * not offer to. The capabilities are the matrix, and they have their own
 * write so the audit trail can answer «what changed» rather than «what it is
 * now».
 *
 * So this is the human half of a role: what it is called, and what it is for.
 * A role called `TENANT_STAFF` that nobody can describe is one nobody will
 * assign correctly.
 */
export function updateRole(
  roleId: string,
  input: { name?: string; description?: string | null },
): Promise<RoleChangeResult> {
  return roleRequest(`/platform/roles/${encodeURIComponent(roleId)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
}

export interface NewRole {
  /**
   * OPTIONAL — the server derives it from the name («Senter resepsjon» →
   * `SENTER_RESEPSJON`) and suffixes a collision, so two clubs may both have a
   * «Resepsjon». The panel does not ask for one: it is an identifier in a
   * format an operator has no reason to know, for a value they never see again.
   */
  key?: string;
  name: string;
  description?: string;
  scope: 'PLATFORM' | 'ORGANIZATION' | 'LINK';
  appliesTo?: 'CENTRE' | 'CLUB' | null;
  capabilities?: string[];
  availability?: 'PLATFORM' | 'SELECTED';
  /** Only with `availability: 'SELECTED'`; the server refuses them otherwise. */
  organizationIds?: string[];
}

export function createRole(input: NewRole): Promise<RoleChangeResult> {
  return roleRequest('/platform/roles', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
}

export type DeleteRoleResult =
  | { state: 'ok' }
  | { state: 'forbidden' }
  | { state: 'refused'; reason: string; holders?: number }
  | { state: 'error'; status: number | null };

export async function deleteRole(roleId: string): Promise<DeleteRoleResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(`/platform/roles/${encodeURIComponent(roleId)}`, {
      method: 'DELETE',
    });
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
        holders?: number;
      } | null;
      return { state: 'refused', reason: body?.error ?? 'refused', holders: body?.holders };
    }
    if (!res.ok) return { state: 'error', status: res.status };
    return { state: 'ok' };
  } catch {
    return { state: 'error', status: null };
  }
}

// ─────────────────────────────────────────────────────────────────────
// AVAILABILITY (#535): which organizations a custom role is offered to.
// ─────────────────────────────────────────────────────────────────────

export type AvailabilityResult =
  | { state: 'ok'; role: RoleView }
  /**
   * Withdrawing an offer from organizations whose members still hold the role.
   * Refused rather than resolved: the two silent resolutions are «strip
   * authority from real people» and «let the model lie».
   */
  | { state: 'in-use'; message: string; organizations: { id: string; name: string }[] }
  /** A refusal the page should explain — the server names which rule. */
  | { state: 'refused'; reason: string; value?: unknown }
  | { state: 'forbidden' }
  | { state: 'error'; status: number | null };

/**
 * Replace which organizations a role is offered to.
 *
 * THE FLAG AND THE SET TRAVEL TOGETHER, matching the server: «platform-wide →
 * these three clubs» is one decision, and splitting it would leave a window
 * where a role is SELECTED with nobody selected — grantable nowhere, by
 * nobody's intent.
 */
export async function updateRoleAvailability(
  roleId: string,
  input: { availability: 'PLATFORM' | 'SELECTED'; organizationIds?: string[] },
): Promise<AvailabilityResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(`/platform/roles/${encodeURIComponent(roleId)}/organizations`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        availability: input.availability,
        // Sent only where it is meaningful: the server refuses ids alongside
        // PLATFORM rather than ignoring them, and it is right to.
        ...(input.availability === 'SELECTED'
          ? { organizationIds: input.organizationIds ?? [] }
          : {}),
      }),
    });
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as {
        message?: string;
        organizations?: { id: string; name: string }[];
      } | null;
      return {
        state: 'in-use',
        message: body?.message ?? 'Rollen er i bruk.',
        organizations: body?.organizations ?? [],
      };
    }
    if (res.status === 400) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
        value?: unknown;
      } | null;
      return { state: 'refused', reason: body?.error ?? 'refused', value: body?.value };
    }
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { role: RoleView };
    return { state: 'ok', role: body.role };
  } catch {
    return { state: 'error', status: null };
  }
}
